// Data prototipe. Mendukung banyak grup klien. Grup aktif dipublikasikan lewat live binding
// (PERSONS, COMPANIES, STEPS, dst.) sehingga model & komponen tidak perlu tahu soal multi-grup.
// Nama orang & PT hanya contoh — hasil akhir mengikuti akta yang diunggah user nanti.

export type CatId = string;

export interface Version {
  step: number; dasar: number; ditempatkan: number; nominal: number;
  holders: [string, number][]; officers: [string, string][]; term?: string; srcHold: string;
}
export interface HistoryEntry { step: number; t: string; p: string; s: string; docUrl?: string; }
export interface KbliEntry { code: string; title: string; unverified?: boolean; main?: boolean; }
export interface DocEntry {
  kind: "pendirian" | "perubahan" | "nib" | "npwp";
  label: string;
  date?: string;
  url: string | null;   // null = belum ada di arsip
  storagePath?: string; // path Object Storage (PDF hasil unggah AI) — URL dibangun saat render
  step?: number;        // untuk menautkan ke entri Riwayat akta
  inBundle?: string;    // catatan: tergabung dalam bundel lain
}
export interface Company {
  id: string; name: string; short: string; cat: CatId; order: number;
  kedudukan: string; alamat: string | null; alamatSrc?: string;
  operasional: string | null; kpp: string | null; kppNote?: string; npwp?: string | null;
  kbli: KbliEntry[]; kbliSrc: string; versions: Version[]; history: HistoryEntry[];
  documents: DocEntry[];
}
export interface Person { id: string; name: string; variants: string[]; }
export interface Category { name: string; color: string; }
export interface Step { date: string; label: string; detail: string; }
export interface StaticFlag { sev: "red" | "yellow" | "info"; code: string; company: string; from: number; msg: string; s?: string; }

interface RawGroup {
  name: string;
  persons: Record<string, { name: string; variants: string[] }>;
  personOrder: string[];
  categories: Record<string, Category>;
  steps: Step[];
  companies: Record<string, any>;
  staticFlags: StaticFlag[];
}

// Daftar kategori manual (mengatur warna pita kartu & legenda saja, bukan hasil kalkulasi).
// Diselaraskan dengan kategori usaha KBLI (A–U) AHU/OSS + kategori struktur grup.
const DEFAULT_CATEGORIES: Record<string, Category> = {
  // Struktur grup
  holding: { name: "Holding", color: "#1b7f8c" },
  subholding: { name: "Sub-Holding", color: "#2f9fae" },
  spv: { name: "SPV / Perusahaan Khusus", color: "#556b8d" },
  operasional: { name: "Perusahaan Operasional", color: "#6b7c99" },
  // Sektor usaha (mengacu kategori KBLI)
  agri: { name: "Pertanian, Kehutanan & Perikanan", color: "#4f8f3a" },
  tambang: { name: "Pertambangan & Penggalian", color: "#6d5636" },
  manufaktur: { name: "Manufaktur / Industri Pengolahan", color: "#9c4f2a" },
  energi: { name: "Ketenagalistrikan, Gas & Energi", color: "#c99a1f" },
  air_limbah: { name: "Pengelolaan Air, Sampah & Limbah", color: "#2f8f8f" },
  konstruksi: { name: "Konstruksi", color: "#b56a1e" },
  grosir: { name: "Perdagangan Besar", color: "#a9791c" },
  retail: { name: "Perdagangan Eceran", color: "#c07f0a" },
  otomotif: { name: "Otomotif & Reparasi Kendaraan", color: "#8a6a3b" },
  farmasi_alkes: { name: "Farmasi & Alat Kesehatan", color: "#4a86a8" },
  logistik: { name: "Pengangkutan & Pergudangan", color: "#3a6ea5" },
  akomodasi: { name: "Akomodasi & Pariwisata", color: "#a24f8c" },
  fnb: { name: "Makanan & Minuman (F&B)", color: "#c0563f" },
  infokom: { name: "Informasi, Komunikasi & Teknologi", color: "#4a5fc7" },
  keuangan: { name: "Jasa Keuangan & Asuransi", color: "#2e7d6b" },
  properti: { name: "Real Estat & Properti", color: "#8a7b2e" },
  profesional: { name: "Jasa Profesional, Ilmiah & Teknis", color: "#5a5fa0" },
  jasa_penunjang: { name: "Penyewaan & Jasa Penunjang Usaha", color: "#6a7a5a" },
  pendidikan: { name: "Pendidikan", color: "#3a8fb0" },
  health: { name: "Layanan Kesehatan", color: "#3d8b58" },
  sosial: { name: "Aktivitas Sosial & Kemasyarakatan", color: "#5aa06a" },
  hiburan: { name: "Kesenian, Hiburan & Rekreasi", color: "#b0508a" },
  media: { name: "Media & Industri Kreatif", color: "#9c4f8c" },
  jasa_lain: { name: "Jasa Lainnya", color: "#7a7a8a" },
  // Lainnya
  none: { name: "Belum dikategorikan", color: "#8a94a3" },
};

// ---------------- GRUP 1: Win Medika ----------------
const rawWinMedika: RawGroup = {
  name: "Win Medika",
  persons: {
    naufal: { name: "Muhammad Naufal", variants: ["MUHAMMAD NAUVAL (Akta Citra No. 6)"] },
    muhammad: { name: "Muhammad", variants: [] },
    windy: { name: "Windy Mayang Sari", variants: [] },
  },
  personOrder: ["naufal", "muhammad", "windy"],
  categories: DEFAULT_CATEGORIES,
  steps: [
    { date: "2022-11-14", label: "PT Win Medika Citra berdiri", detail: "Akta No. 37 tgl 31-10-2022, SK AHU-0079342.AH.01.01.Tahun 2022" },
    { date: "2024-08-08", label: "PT Win Medika Health berdiri", detail: "Akta No. 01 tgl 06-08-2024, SK AHU-0060258.AH.01.01.Tahun 2024" },
    { date: "2024-11-21", label: "PT Win Medika Center berdiri", detail: "Akta No. 28 tgl 19-11-2024, SK AHU-0092330.AH.01.01.Tahun 2024" },
    { date: "2024-12-02", label: "Health: pemecahan nominal & jual saham ke WM Center", detail: "Akta No. 36 tgl 28-11-2024, berlaku sejak SP 02-12-2024" },
    { date: "2024-12-02", label: "Citra: pemecahan nominal & jual saham ke WM Center", detail: "Akta No. 38 tgl 28-11-2024 pukul 18.20, berlaku sejak SP 02-12-2024" },
    { date: "2024-12-02", label: "PT Win Medika Utama berdiri", detail: "Akta No. 40 tgl 28-11-2024, SK AHU-0095617.AH.01.01.Tahun 2024" },
    { date: "2024-12-02", label: "Health: WM Center jadi 99,9% & pengurus baru", detail: "Akta No. 4 tgl 02-12-2024, SP AHU-AH.01.09-0282404" },
    { date: "2024-12-02", label: "Citra: WM Center jadi 99,9% & pengurus baru", detail: "Akta No. 6 tgl 02-12-2024 pukul 09.00, SP AHU-AH.01.09-0282401" },
    { date: "2025-12-09", label: "PT Win Medika Solusi berdiri", detail: "Akta No. 01 tgl 08-12-2025, SK AHU-0105604.AH.01.01.Tahun 2025" },
  ],
  companies: {
    center: {
      name: "PT Win Medika Center", short: "WM Center", cat: "holding", order: 0,
      kedudukan: "Jakarta Selatan",
      alamat: "Plaza Aminta Lt. 5/504, Jl. TB Simatupang Kav. 10, Pondok Pinang, Kebayoran Lama",
      alamatSrc: "PT_WIN_MEDIKA_CENTER - 2024 Nov - PENDIRIAN, NIB hlm. 29",
      operasional: "Virtual office", kpp: "KPP Pratama Kebayoran Lama", kppNote: "dari bagan lama; NPWP terdaftar 22-11-2024",
      kbli: [["64200", "Aktivitas Perusahaan Holding", false, true], ["64931", "Perusahaan Modal Ventura Konvensional"], ["66199", "Aktivitas Penunjang Jasa Keuangan Lainnya YTDL"], ["66311", "Manajer Investasi"], ["70209", "Aktivitas Konsultasi Manajemen Lainnya"], ["77299", "Penyewaan dan Sewa Guna Usaha Tanpa Hak Opsi Barang Keperluan Rumah Tangga dan Pribadi Lainnya YTDL"], ["77400", "Sewa Guna Usaha Tanpa Hak Opsi Intelektual Properti, Bukan Karya Hak Cipta"]],
      kbliSrc: "Akta No. 28 Pasal 3 (7 KBLI) & NIB 2211240083551 — hanya 64200 direalisasikan di NIB (Kegiatan Utama)",
      versions: [
        { step: 2, dasar: 600000000, ditempatkan: 150000000, nominal: 1000000,
          holders: [["naufal", 50], ["muhammad", 50], ["windy", 50]],
          officers: [["naufal", "Direktur"], ["muhammad", "Komisaris Utama"], ["windy", "Komisaris"]],
          srcHold: "PT_WIN_MEDIKA_CENTER - 2024 Nov - PENDIRIAN, Lampiran SK hlm. 2; Akta No. 28 hlm. 23–24" },
      ],
      history: [{ step: 2, t: "Pendirian", p: "Akta No. 28 tgl 19-11-2024, Notaris Lavanti Meilia Erlinda (Surabaya). SK AHU-0092330.AH.01.01.Tahun 2024 tgl 21-11-2024.", s: "PT_WIN_MEDIKA_CENTER - 2024 Nov - PENDIRIAN, hlm. 1–4" }],
    },
    citra: {
      name: "PT Win Medika Citra", short: "WM Citra", cat: "retail", order: 1,
      kedudukan: "Jakarta Timur", alamat: "Jl. Pemuda No. 66, Rawamangun, Pulogadung",
      alamatSrc: "1. PT WIN MEDIKA CITRA - 2022 Okt - PENDIRIAN, NPWP hlm. 28",
      operasional: "Store", kpp: "KPP Pratama Pulogadung", kppNote: "dari bagan lama",
      kbli: [["47725", "Perdagangan Eceran Alat Laboratorium, Alat Farmasi dan Alat Kesehatan untuk Manusia"], ["82302", "Jasa Penyelenggara Event Khusus (Special Event)"], ["58130", "Penerbitan Surat Kabar, Jurnal dan Buletin atau Majalah"], ["86105", "Aktivitas Klinik Swasta"], ["86201", "Aktivitas Praktik Dokter"], ["86202", "Aktivitas Praktik Dokter Spesialis"], ["86901", "Pelayanan Kesehatan oleh Tenaga Kesehatan Selain Dokter dan Dokter Gigi"], ["86903", "Aktivitas Pelayanan Penunjang Kesehatan"]],
      kbliSrc: "Akta No. 37 tgl 31-10-2022 (maksud & tujuan) dan Lampiran NIB 1511220125153",
      versions: [
        { step: 0, dasar: 60000000, ditempatkan: 15000000, nominal: 500000,
          holders: [["muhammad", 10], ["windy", 10], ["naufal", 10]],
          officers: [["muhammad", "Direktur Utama"], ["windy", "Direktur"], ["naufal", "Komisaris"]],
          srcHold: "1. PT WIN MEDIKA CITRA - 2022 Okt - PENDIRIAN, Lampiran SK hlm. 1" },
        { step: 4, dasar: 60000000, ditempatkan: 15000000, nominal: 1000,
          holders: [["naufal", 5000], ["windy", 2507], ["center", 7493]],
          officers: [["muhammad", "Direktur Utama"], ["windy", "Direktur"], ["naufal", "Komisaris"]],
          srcHold: "2. PT WIN MEDIKA CITRA - 2024 Nov - JB SAHAM (Akta 38), hlm. 7–8" },
        { step: 7, dasar: 60000000, ditempatkan: 15000000, nominal: 1000,
          holders: [["center", 14985], ["naufal", 15]],
          officers: [["naufal", "Direktur"], ["muhammad", "Komisaris"]],
          term: "masa jabatan s.d. 02-12-2029",
          srcHold: "3. PT WIN MEDIKA CITRA - 2024 Dec - JB SAHAM & DIRKOM (Akta 6), hlm. 7" },
      ],
      history: [
        { step: 0, t: "Pendirian", p: "Akta No. 37 tgl 31-10-2022, dibuat Rizky Putra Hanafie sebagai pengganti Notaris Fauzah Askar. SK AHU-0079342.AH.01.01.Tahun 2022 tgl 14-11-2022.", s: "1. PT WIN MEDIKA CITRA - 2022 Okt - PENDIRIAN, hlm. 1–4" },
        { step: 4, t: "Pemecahan nominal & peralihan saham", p: "Akta No. 38 tgl 28-11-2024 (keputusan sirkuler pemecahan nominal) & Akta jual beli saham No. 39, Notaris Lavanti Meilia Erlinda. Nominal Rp500.000 → Rp1.000 (30 → 15.000 lembar). Muhammad menjual 5.000 dan Windy 2.493 lembar ke WM Center. SP AHU-AH.01.09-0282245 & AHU-AH.01.03-0216111 tgl 02-12-2024; Daftar Perseroan AHU-0259895.AH.01.11 Tahun 2024.", s: "2. PT WIN MEDIKA CITRA - 2024 Nov - JB SAHAM (Akta 38/39), hlm. 1–8" },
        { step: 7, t: "Peralihan saham & pengurus baru", p: "Akta No. 6 tgl 02-12-2024, Notaris Lavanti Meilia Erlinda. Muhammad Naufal menjual 4.985 dan Windy 2.507 lembar ke WM Center → WM Center 14.985 lembar, Naufal 15. Direktur: Muhammad Naufal; Komisaris: Muhammad (masa jabatan s.d. 02-12-2029). SP AHU-AH.01.09-0282401; Daftar Perseroan AHU-0260285.AH.01.11 Tahun 2024.", s: "3. PT WIN MEDIKA CITRA - 2024 Dec - JB SAHAM & DIRKOM (Akta 6), hlm. 1–8" },
      ],
    },
    health: {
      name: "PT Win Medika Health", short: "WM Health", cat: "health", order: 2,
      kedudukan: "Jakarta Selatan", alamat: "Jl. Kemang Timur No. 1, Pejaten Barat, Pasar Minggu",
      alamatSrc: "1. PT WIN MEDIKA HEALTH - 2024 Aug - PENDIRIAN, NIB hlm. 29",
      operasional: "Virtual office", kpp: "KPP Pratama Pasar Minggu", kppNote: "dari bagan lama; NPWP terdaftar 08-08-2024",
      kbli: [["82302", "Jasa Penyelenggara Event Khusus (Special Event)"], ["58130", "Penerbitan Surat Kabar, Jurnal dan Buletin atau Majalah"], ["47725", "Perdagangan Eceran Alat Laboratorium, Alat Farmasi dan Alat Kesehatan untuk Manusia"], ["86901", "Aktivitas Pelayanan Kesehatan yang Dilakukan oleh Tenaga Kesehatan Selain Dokter dan Dokter Gigi"], ["86903", "Aktivitas Pelayanan Penunjang Kesehatan"], ["86105", "Aktivitas Klinik Swasta"], ["86201", "Aktivitas Praktik Dokter"], ["86202", "Aktivitas Praktik Dokter Spesialis"]],
      kbliSrc: "Lampiran NIB 1508240069141 & Akta No. 01 tgl 06-08-2024 (maksud & tujuan)",
      versions: [
        { step: 1, dasar: 60000000, ditempatkan: 15000000, nominal: 500000,
          holders: [["naufal", 10], ["windy", 10], ["muhammad", 10]],
          officers: [["naufal", "Direktur Utama"], ["windy", "Direktur"], ["muhammad", "Komisaris"]],
          srcHold: "1. PT WIN MEDIKA HEALTH - 2024 Aug - PENDIRIAN, Lampiran SK hlm. 2" },
        { step: 3, dasar: 60000000, ditempatkan: 15000000, nominal: 1000,
          holders: [["naufal", 5000], ["windy", 2507], ["center", 7493]],
          officers: [["naufal", "Direktur Utama"], ["windy", "Direktur"], ["muhammad", "Komisaris"]],
          srcHold: "2. PT WIN MEDIKA HEALTH - 2024 Nov - JB SAHAM (Akta 36), hlm. 8–9" },
        { step: 6, dasar: 60000000, ditempatkan: 15000000, nominal: 1000,
          holders: [["center", 14985], ["windy", 15]],
          officers: [["windy", "Direktur Utama"], ["naufal", "Komisaris Utama"]],
          term: "masa jabatan s.d. 02-12-2029",
          srcHold: "3. PT WIN MEDIKA HEALTH - 2024 Dec - JB SAHAM & DIRKOM (Akta 4), hlm. 7" },
      ],
      history: [
        { step: 1, t: "Pendirian", p: "Akta No. 01 tgl 06-08-2024, Notaris Rizky Putra Hanafie (Kab. Sukabumi). SK AHU-0060258.AH.01.01.Tahun 2024 tgl 08-08-2024.", s: "1. PT WIN MEDIKA HEALTH - 2024 Aug - PENDIRIAN, hlm. 1–2" },
        { step: 3, t: "Pemecahan nominal & peralihan saham", p: "Akta No. 36 tgl 28-11-2024, Notaris Lavanti Meilia Erlinda. Nominal Rp500.000 → Rp1.000 (30 → 15.000 lembar). Muhammad menjual 5.000 dan Windy 2.493 lembar ke WM Center. SP AHU-AH.01.03-0216113 & AHU-AH.01.09-0282249 tgl 02-12-2024.", s: "2. PT WIN MEDIKA HEALTH - 2024 Nov - JB SAHAM (Akta 36), hlm. 1–9" },
        { step: 6, t: "Peralihan saham & pengurus baru", p: "Akta No. 4 tgl 02-12-2024, Notaris Lavanti Meilia Erlinda. Muhammad Naufal menjual 5.000 dan Windy 2.492 lembar ke WM Center → WM Center 14.985 lembar, Windy 15. Direktur Utama: Windy Mayang Sari; Komisaris Utama: Muhammad Naufal (masa jabatan s.d. 02-12-2029). SP AHU-AH.01.09-0282404; Daftar Perseroan AHU-0260289.AH.01.11 Tahun 2024.", s: "3. PT WIN MEDIKA HEALTH - 2024 Dec - JB SAHAM & DIRKOM (Akta 4), hlm. 1–8" },
      ],
    },
    utama: {
      name: "PT Win Medika Utama", short: "WM Utama", cat: "retail", order: 3,
      kedudukan: "Kota Bandung", alamat: "Lima Building, Jl. Sunda No. 59 Lt. 1 Blok J, Kebon Pisang, Sumur Bandung, Kota Bandung 40112",
      alamatSrc: "PT_WIN_MEDIKA_UTAMA - 2024 Nov - PENDIRIAN, NIB 0312240022123 hlm. 29",
      operasional: "Store", kpp: "KPP Pratama Bandung Cibeunying", kppNote: "NPWP 28.616.546.9-423.000 terdaftar 02-12-2024",
      kbli: [["47725", "Perdagangan Eceran Alat Laboratorium, Alat Farmasi dan Alat Kesehatan untuk Manusia", false, true], ["58130", "Penerbitan Surat Kabar, Jurnal dan Buletin atau Majalah"], ["82302", "Jasa Penyelenggara Event Khusus (Special Event)"], ["86105", "Aktivitas Klinik Swasta"], ["86201", "Aktivitas Praktik Dokter"], ["86202", "Aktivitas Praktik Dokter Spesialis"], ["86901", "Aktivitas Pelayanan Kesehatan yang Dilakukan oleh Tenaga Kesehatan Selain Dokter dan Dokter Gigi"], ["86903", "Aktivitas Pelayanan Penunjang Kesehatan"]],
      kbliSrc: "Lampiran NIB 0312240022123 (47725 = Kegiatan Utama) & Akta No. 40 Pasal 3",
      versions: [
        { step: 5, dasar: 200000000, ditempatkan: 50000000, nominal: 10000,
          holders: [["center", 4995], ["muhammad", 5]],
          officers: [["muhammad", "Direktur"], ["windy", "Komisaris"]],
          srcHold: "PT_WIN_MEDIKA_UTAMA - 2024 Nov - PENDIRIAN, Lampiran SK hlm. 2" },
      ],
      history: [{ step: 5, t: "Pendirian", p: "Akta No. 40 tgl 28-11-2024, Notaris Lavanti Meilia Erlinda (Surabaya). SK AHU-0095617.AH.01.01.Tahun 2024 tgl 02-12-2024.", s: "PT_WIN_MEDIKA_UTAMA - 2024 Nov - PENDIRIAN, hlm. 1–4" }],
    },
    solusi: {
      name: "PT Win Medika Solusi", short: "WM Solusi", cat: "none", order: 4,
      kedudukan: "Kota Surabaya", alamat: null, operasional: null, kpp: null,
      kbli: [["47725", "Perdagangan Eceran Alat Laboratorium, Alat Farmasi dan Alat Kesehatan untuk Manusia"], ["58130", "Penerbitan Surat Kabar, Jurnal dan Buletin atau Majalah"], ["82302", "Jasa Penyelenggara Event Khusus (Special Event)"], ["86105", "Aktivitas Klinik Swasta"], ["86201", "Aktivitas Praktik Dokter"], ["86202", "Aktivitas Praktik Dokter Spesialis"], ["86901", "Aktivitas Pelayanan Kesehatan yang Dilakukan oleh Tenaga Kesehatan Selain Dokter dan Dokter Gigi"], ["86903", "Aktivitas Pelayanan Penunjang Kesehatan"]],
      kbliSrc: "Akta No. 01 Pasal 3, hlm. 7–8 (NIB belum terbit — KBLI dari akta)",
      versions: [
        { step: 8, dasar: 200000000, ditempatkan: 50000000, nominal: 50000,
          holders: [["center", 999], ["muhammad", 1]],
          officers: [["muhammad", "Direktur Utama"], ["naufal", "Direktur"], ["windy", "Komisaris"]],
          srcHold: "PT_WIN_MEDIKA_SOLUSI - PENDIRIAN, Lampiran SK hlm. 2" },
      ],
      history: [{ step: 8, t: "Pendirian", p: "Akta No. 01 tgl 08-12-2025, Notaris Mia Louisa Suwandhani (Kab. Kediri). WM Center diwakili Muhammad Naufal selaku Direktur, dengan persetujuan Dewan Komisaris tgl 05-12-2025. SK AHU-0105604.AH.01.01.Tahun 2025 tgl 09-12-2025.", s: "PT_WIN_MEDIKA_SOLUSI - PENDIRIAN, hlm. 1–5" }],
    },
  },
  staticFlags: [
    { sev: "yellow", code: "KONFLIK_REFERENSI", company: "citra", from: 4, msg: 'Recital Akta No. 38 dan No. 6 menyebut akta pendirian Citra "nomor 01", sedangkan SK AHU-0079342 dan sampul akta menyebut Akta No. 37 tgl 31-10-2022.', s: "Sumber: file Citra 2022 hlm. 2–4; Akta No. 38 hlm. 4; Akta No. 6 hlm. 3–4" },
    { sev: "info", code: "VARIASI_NAMA", company: "citra", from: 7, msg: 'Akta No. 6 menulis penjual sebagai "MUHAMMAD NAUVAL". Digabung ke Muhammad Naufal karena NIK sama.', s: "Sumber: Akta Citra No. 6 hlm. 6" },
    { sev: "yellow", code: "DOK_BELUM_ADA", company: "solusi", from: 8, msg: "PT Win Medika Solusi baru tahap pendirian (Akta No.01 08-12-2025): akta hanya menyebut kedudukan Kota Surabaya, NIB & NPWP belum terbit — alamat lengkap, KPP, dan domisili operasional belum tersedia." },
  ],
};

// ---------------- Builder ----------------
export interface BuiltGroup {
  name: string;
  persons: Record<string, Person>;
  personOrder: string[];
  categories: Record<string, Category>;
  steps: Step[];
  companies: Record<string, Company>;
  companyOrder: string[];
  staticFlags: StaticFlag[];
}

// ---------------- Arsip dokumen (URL publik dari file yang diunggah user) ----------------
const ART = "https://customer-assets-wrfwihn1.emergentagent.net/job_ownership-map/artifacts/";
const U_CENTER = ART + "kngzj8jn_PT%20WIN%20MEDIKA%20CENTER%20-%202024%20Nov%20-%20PENDIRIAN%20-%20%28SK%2C%20AKTA%2C%20NPWP%2C%20NIB%29.pdf";
const U_CITRA_37 = ART + "usmwuwq1_1.%20PT.%20WIN%20MEDIKA%20CITRA%20-%202022%20Okt%20-%20PENDIRIAN%20-%20%28SK%2C%20AKTA%2C%20NIB%2C%20NPWP%29.pdf";
const U_CITRA_3839 = ART + "x3jl7rd7_2.%20PT.%20WIN%20MEDIKA%20CITRA%20-%202024%20Nov%20-%20JB%20SAHAM%20KE%20PT%20WM%20CENTER%20-%20%28SP%2C%20AKTA%20CR.38%2C%20AKTA%20JB%20SAHAM%2C%20CR%20PT%29.pdf";
const U_CITRA_6 = ART + "iuldnmid_3.%20PT.%20WIN%20MEDIKA%20CITRA%20-%202024%20Dec%20-%20JB%20SAHAM%20KE%20PT%20WM%20CENTER%2C%20DIRKOM%20-%20%28SP%2C%20AKTA%20CR.06%2C%20AKTA%20JB%20SAHAM%2C%20CR%20PT%29.pdf";
const U_HEALTH_1 = ART + "j2oppy3m_1.%20PT.%20WIN%20MEDIKA%20HEALTH%20-%202024%20Aug%20-%20PENDIRIAN%20-%20%28SK%2C%20AKTA%2C%20NIB%2C%20NPWP%29.pdf";
const U_HEALTH_36 = ART + "6kel1js4_2.%20PT.%20WIN%20MEDIKA%20HEALTH%20-%202024%20Nov%20-%20JB%20SAHAM%20ke%20PT%20WM%20CTR%201-%28SP%2C%20AKTA%20CR.36%2C%20AKTA%20JBSAHAM%2C%20CR%20PT%29.pdf";
const U_HEALTH_4 = ART + "y6ipbj61_3.%20PT.%20WIN%20MEDIKA%20HEALTH%20-%202024%20Dec%20-%20JB%20SAHAM%20ke%20PT%20WM%20CTR%202%2C%20DIRKOM%20-%20%28SP%2C%20AKTA%20CR.4%2C%20AKTA%20JBSAHAM%2C%20CR%20PT%29.pdf";
const U_UTAMA = ART + "oyx2szbu_PT%20WIN%20MEDIKA%20UTAMA%20-%202024%20Nov%20-%20PENDIRIAN%20-%20%28SK%2C%20AKTA%2C%20NPWP%2C%20NIB%29.pdf";
const U_SOLUSI = ART + "avrgmntt_PT%20WIN%20MEDIKA%20SOLUSI%20-%20PENDIRIAN%20-%20%28SK%2C%20AKTA%29.pdf";

const DOCS_BY_NAME: Record<string, DocEntry[]> = {
  "PT Win Medika Center": [
    { kind: "pendirian", label: "Akta Pendirian No. 28", date: "2024-11-19", url: U_CENTER, step: 2 },
    { kind: "nib", label: "NIB 2211240083551", url: U_CENTER, inBundle: "bundel pendirian" },
    { kind: "npwp", label: "NPWP", url: U_CENTER, inBundle: "bundel pendirian" },
  ],
  "PT Win Medika Citra": [
    { kind: "pendirian", label: "Akta Pendirian No. 37", date: "2022-10-31", url: U_CITRA_37, step: 0 },
    { kind: "perubahan", label: "Akta Perubahan No. 38/39 — JB Saham", date: "2024-11-28", url: U_CITRA_3839, step: 4 },
    { kind: "perubahan", label: "Akta Perubahan No. 6 — JB Saham & Dirkom", date: "2024-12-02", url: U_CITRA_6, step: 7 },
    { kind: "nib", label: "NIB 1511220125153", url: U_CITRA_37, inBundle: "bundel pendirian" },
    { kind: "npwp", label: "NPWP", url: U_CITRA_37, inBundle: "bundel pendirian" },
  ],
  "PT Win Medika Health": [
    { kind: "pendirian", label: "Akta Pendirian No. 01", date: "2024-08-06", url: U_HEALTH_1, step: 1 },
    { kind: "perubahan", label: "Akta Perubahan No. 36 — JB Saham", date: "2024-11-28", url: U_HEALTH_36, step: 3 },
    { kind: "perubahan", label: "Akta Perubahan No. 4 — JB Saham & Dirkom", date: "2024-12-02", url: U_HEALTH_4, step: 6 },
    { kind: "nib", label: "NIB 1508240069141", url: U_HEALTH_1, inBundle: "bundel pendirian" },
    { kind: "npwp", label: "NPWP", url: U_HEALTH_1, inBundle: "bundel pendirian" },
  ],
  "PT Win Medika Utama": [
    { kind: "pendirian", label: "Akta Pendirian No. 40", date: "2024-11-28", url: U_UTAMA, step: 5 },
    { kind: "nib", label: "NIB 0312240022123", url: U_UTAMA, inBundle: "bundel pendirian" },
    { kind: "npwp", label: "NPWP 28.616.546.9-423.000", url: U_UTAMA, inBundle: "bundel pendirian" },
  ],
  "PT Win Medika Solusi": [
    { kind: "pendirian", label: "Akta Pendirian No. 01", date: "2025-12-08", url: U_SOLUSI, step: 8 },
    { kind: "nib", label: "NIB", url: null },
    { kind: "npwp", label: "NPWP", url: null },
  ],
};

function buildGroup(raw: RawGroup): BuiltGroup {
  const persons: Record<string, Person> = {};
  raw.personOrder.forEach((id) => { persons[id] = { id, name: raw.persons[id].name, variants: raw.persons[id].variants }; });
  const companies: Record<string, Company> = {};
  Object.keys(raw.companies).forEach((id) => {
    const c = raw.companies[id];
    const docs: DocEntry[] = (c.documents as DocEntry[]) ?? DOCS_BY_NAME[c.name] ?? [];
    const history: HistoryEntry[] = (c.history as HistoryEntry[]).map((h) => {
      const d = docs.find((x) => x.step === h.step && (x.kind === "pendirian" || x.kind === "perubahan"));
      return d && (d.url || d.storagePath) ? { ...h, docUrl: d.url || undefined } : h;
    });
    companies[id] = {
      id, name: c.name, short: c.short, cat: c.cat, order: c.order,
      kedudukan: c.kedudukan, alamat: c.alamat ?? null, alamatSrc: c.alamatSrc,
      operasional: c.operasional ?? null, kpp: c.kpp ?? null, kppNote: c.kppNote, npwp: c.npwp ?? null,
      kbli: (c.kbli as any[]).map((k) => ({ code: k[0], title: k[1], unverified: k[2] === true, main: k[3] === true })),
      kbliSrc: c.kbliSrc,
      versions: c.versions.map((v: any) => ({
        step: v.step, dasar: v.dasar, ditempatkan: v.ditempatkan, nominal: v.nominal,
        holders: v.holders, officers: v.officers, term: v.term, srcHold: v.srcHold,
      })),
      history,
      documents: docs,
    };
  });
  const companyOrder = Object.keys(companies).sort((a, b) => companies[a].order - companies[b].order);
  return {
    name: raw.name, persons, personOrder: raw.personOrder, categories: raw.categories,
    steps: raw.steps, companies, companyOrder, staticFlags: raw.staticFlags,
  };
}

function emptyGroup(name: string): BuiltGroup {
  return { name, persons: {}, personOrder: [], categories: DEFAULT_CATEGORIES, steps: [], companies: {}, companyOrder: [], staticFlags: [] };
}

// ---------------- Registry & grup aktif ----------------
// Tidak ada lagi grup bawaan hardcoded. Data contoh Win Medika di-seed ke workspace
// tiap user saat pertama login (lihat Root.tsx) lalu tersimpan permanen di database.
export const BUILTIN_GROUP_IDS: string[] = [];
export const WINMEDIKA_SEED_ID = "winmedika";
export const WINMEDIKA_SEED_NAME = "Win Medika";
const REGISTRY: Record<string, BuiltGroup> = {};
const ORDER: string[] = [];

export const DEFAULT_GROUP_ID = WINMEDIKA_SEED_ID;

// Salinan segar (deep clone) data contoh Win Medika untuk di-seed ke workspace user.
export function buildWinMedikaSeed(): BuiltGroup {
  return JSON.parse(JSON.stringify(buildGroup(rawWinMedika)));
}

// ---------------- Bangun grup dari hasil Ekstraksi AI ----------------
export interface ExtractedShareholder { name: string; entity_type?: string; shares?: number | null; nominal_value?: number | null; percentage?: number | null; }
export interface ExtractedKbli { code: string; title?: string; main?: boolean; }
export interface ExtractionResult {
  company_name?: string | null; legal_form?: string | null; deed_number?: string | null; deed_date?: string | null;
  domicile?: string | null; npwp?: string | null; kpp?: string | null; address?: string | null;
  modal_dasar?: number | null; modal_ditempatkan?: number | null; modal_disetor?: number | null;
  total_shares?: number | null; nominal_per_share?: number | null; kbli?: ExtractedKbli[] | null;
  shareholders?: ExtractedShareholder[]; management?: { name: string; role: string }[];
  overall_confidence?: number; notes?: string | null;
}

function mkId(name: string, used: Set<string>): string {
  const base = (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 16) || "o";
  let id = base, i = 2;
  while (used.has(id)) { id = base + i; i++; }
  used.add(id);
  return id;
}

export interface ExtractionDoc extends ExtractionResult { storage_path?: string | null; filename?: string; doc_kind?: DocEntry["kind"]; }

// Ubah SATU atau BEBERAPA akta (satu PT) menjadi struktur grup valid dengan banyak tahap.
export function groupFromExtractions(docsIn: ExtractionDoc[], groupName: string): BuiltGroup {
  const docs = [...docsIn].sort((a, b) => (a.deed_date || "").localeCompare(b.deed_date || ""));
  const used = new Set<string>();
  const persons: Record<string, { name: string; variants: string[] }> = {};
  const personOrder: string[] = [];
  const nameToId: Record<string, string> = {};
  const getId = (nm: string) => {
    const key = (nm || "").trim().toUpperCase();
    if (nameToId[key]) return nameToId[key];
    const id = mkId(nm, used);
    persons[id] = { name: (nm || "").trim() || "Tanpa nama", variants: [] };
    personOrder.push(id);
    nameToId[key] = id;
    return id;
  };

  const first = docs[0] || {};
  const cname = (first.company_name || docs.find((d) => d.company_name)?.company_name || groupName || "Perusahaan").trim();
  const short = cname.replace(/^\s*(PT|CV)\.?\s+/i, "").trim() || cname;
  const domicile = first.domicile || docs.find((d) => d.domicile)?.domicile || "—";

  // KBLI: gabungan seluruh dokumen (unik per code), tandai main bila ada.
  const kbliMap: Record<string, { code: string; title: string; main: boolean }> = {};
  for (const d of docs) for (const k of (d.kbli || [])) {
    if (!k.code) continue;
    const cur = kbliMap[k.code];
    kbliMap[k.code] = { code: k.code, title: k.title || cur?.title || k.code, main: !!k.main || !!cur?.main };
  }
  const kbliTuples = Object.values(kbliMap).map((k) => [k.code, k.title, false, k.main]);

  const steps: Step[] = [];
  const versions: any[] = [];
  const documents: DocEntry[] = [];
  docs.forEach((d, i) => {
    const deedInfo = [d.deed_number ? `Akta No. ${d.deed_number}` : null, d.deed_date ? `tgl ${d.deed_date}` : null].filter(Boolean).join(" ");
    steps.push({
      date: d.deed_date || new Date().toISOString().slice(0, 10),
      label: `${cname}${deedInfo ? " — " + deedInfo : (i === 0 ? " berdiri" : " perubahan")}`,
      detail: d.notes || deedInfo || "Diusulkan oleh Ekstraksi AI",
    });
    const holders: [string, number][] = (d.shareholders || []).map((s) => {
      const shares = (s.shares != null ? Number(s.shares) : (s.percentage != null ? Number(s.percentage) : 0)) || 0;
      return [getId(s.name), shares];
    });
    const officers: [string, string][] = (d.management || []).map((m) => [getId(m.name), m.role || "Pengurus"]);
    versions.push({
      step: i, dasar: Number(d.modal_dasar) || 0, ditempatkan: Number(d.modal_ditempatkan) || 0,
      nominal: Number(d.nominal_per_share) || 0, holders, officers,
      srcHold: `Ekstraksi AI${d.filename ? " — " + d.filename : ""}`,
    });
    const hasKbli = (d.kbli || []).length > 0;
    const kind: DocEntry["kind"] = i === 0 ? "pendirian" : (hasKbli && (d.shareholders || []).length === 0 ? "nib" : "perubahan");
    documents.push({
      kind,
      label: `${kind === "pendirian" ? "Akta Pendirian" : kind === "nib" ? "NIB / Lampiran" : "Akta Perubahan"}${d.deed_number ? " No. " + d.deed_number : ""}`,
      date: d.deed_date || undefined,
      url: null,
      storagePath: d.storage_path || undefined,
      step: i,
    });
  });

  const mainKbli = Object.values(kbliMap).find((k) => k.main)?.code || Object.values(kbliMap)[0]?.code;
  const cat = (mainKbli && suggestCategoryFromKbli(mainKbli)) || "none";

  const raw: RawGroup = {
    name: groupName || cname,
    persons, personOrder,
    categories: DEFAULT_CATEGORIES,
    steps: steps.length ? steps : [{ date: new Date().toISOString().slice(0, 10), label: cname, detail: "Ekstraksi AI" }],
    companies: {
      co: {
        name: cname, short, cat, order: 0,
        kedudukan: domicile, alamat: null, operasional: null, kpp: null,
        kbli: kbliTuples, kbliSrc: "Ekstraksi AI (gabungan dokumen)",
        versions, documents,
        history: docs.map((d, i) => ({
          step: i, t: i === 0 ? "Pendirian (AI)" : "Perubahan (AI)",
          p: d.notes || [d.deed_number ? `Akta No. ${d.deed_number}` : null, d.deed_date ? `tgl ${d.deed_date}` : null].filter(Boolean).join(" ") || "Ekstraksi AI",
          s: "Diusulkan AI, ditinjau konsultan",
        })),
      },
    },
    staticFlags: [],
  };
  return buildGroup(raw);
}

// Kompatibilitas: satu dokumen.
export function groupFromExtraction(ext: ExtractionResult, groupName: string): BuiltGroup {
  return groupFromExtractions([ext as ExtractionDoc], groupName);
}

// Daftar nama PT dalam sebuah grup (untuk cek duplikat sebelum menambah PT).
export function companyNamesOf(g: BuiltGroup): string[] {
  return (g.companyOrder || []).map((cid) => g.companies[cid]?.name || "").filter(Boolean);
}

function normPtName(s: string): string {
  return (s || "").trim().toUpperCase().replace(/^(PT|CV)\.?\s+/i, "").replace(/\s+/g, " ");
}

// Cek apakah nama PT sudah ada di grup (abaikan prefix PT/CV & spasi).
export function groupHasCompany(g: BuiltGroup, companyName: string): boolean {
  const target = normPtName(companyName);
  if (!target) return false;
  return companyNamesOf(g).some((n) => normPtName(n) === target);
}

// Tambahkan PT baru (dari 1+ akta) ke grup yang SUDAH ADA. Timeline digabung
// berdasarkan tanggal akta; orang bernama sama dihubungkan lintas-PT.
export function mergeExtractionsIntoGroup(existingIn: BuiltGroup, docsIn: ExtractionDoc[]): BuiltGroup {
  const existing: BuiltGroup = JSON.parse(JSON.stringify(existingIn));
  const docs = [...docsIn].sort((a, b) => (a.deed_date || "").localeCompare(b.deed_date || ""));

  // Resolusi orang terhadap grup yang ada (dedupe berdasarkan nama & varian).
  const usedIds = new Set<string>([...existing.personOrder, ...existing.companyOrder]);
  const persons = existing.persons;
  const personOrder = existing.personOrder;
  const nameToId: Record<string, string> = {};
  for (const pid of personOrder) {
    const p = persons[pid];
    if (!p) continue;
    nameToId[(p.name || "").trim().toUpperCase()] = pid;
    for (const v of (p.variants || [])) nameToId[(v || "").trim().toUpperCase()] = pid;
  }
  const getId = (nm: string) => {
    const key = (nm || "").trim().toUpperCase();
    if (nameToId[key]) return nameToId[key];
    const id = mkId(nm, usedIds);
    persons[id] = { id, name: (nm || "").trim() || "Tanpa nama", variants: [] };
    personOrder.push(id);
    nameToId[key] = id;
    return id;
  };

  const first = docs[0] || {};
  const cname = (first.company_name || docs.find((d) => d.company_name)?.company_name || "Perusahaan").trim();
  const short = cname.replace(/^\s*(PT|CV)\.?\s+/i, "").trim() || cname;
  const domicile = first.domicile || docs.find((d) => d.domicile)?.domicile || "—";

  const kbliMap: Record<string, { code: string; title: string; main: boolean }> = {};
  for (const d of docs) for (const k of (d.kbli || [])) {
    if (!k.code) continue;
    const cur = kbliMap[k.code];
    kbliMap[k.code] = { code: k.code, title: k.title || cur?.title || k.code, main: !!k.main || !!cur?.main };
  }
  const kbli: KbliEntry[] = Object.values(kbliMap).map((k) => ({ code: k.code, title: k.title, unverified: false, main: k.main }));
  const mainKbli = Object.values(kbliMap).find((k) => k.main)?.code || Object.values(kbliMap)[0]?.code;
  const cat = (mainKbli && suggestCategoryFromKbli(mainKbli)) || "none";

  // Timeline gabungan: PERTAHANKAN semua langkah existing (bisa ada >1 langkah pada
  // tanggal yang sama), lalu sisipkan langkah baru untuk tanggal akta yang belum ada.
  const existingSteps = existing.steps;
  const existingDateSet = new Set(existingSteps.map((s) => s.date));
  const docDates = docs.map((d) => d.deed_date || new Date().toISOString().slice(0, 10));
  const newDates = Array.from(new Set(docDates.filter((dt) => !existingDateSet.has(dt))));

  const docByDate: Record<string, ExtractionDoc> = {};
  docs.forEach((d, i) => { docByDate[docDates[i]] = d; });

  type Slot = { date: string; step: Step; orig?: number };
  const slots: Slot[] = [];
  existingSteps.forEach((s, i) => slots.push({ date: s.date, step: s, orig: i }));
  for (const dt of newDates) {
    const d = docByDate[dt];
    const deedInfo = d ? [d.deed_number ? `Akta No. ${d.deed_number}` : null, `tgl ${dt}`].filter(Boolean).join(" ") : "";
    slots.push({ date: dt, step: { date: dt, label: `${cname}${deedInfo ? " — " + deedInfo : ""}`, detail: (d?.notes) || deedInfo || "Ekstraksi AI" } });
  }
  const order = slots.map((_, i) => i).sort((a, b) => slots[a].date.localeCompare(slots[b].date) || (a - b));
  const newSteps: Step[] = order.map((i) => slots[i].step);
  const remapExisting: Record<number, number> = {};
  const dateToFinal: Record<string, number> = {};
  order.forEach((slotI, finalI) => {
    const s = slots[slotI];
    if (s.orig != null) remapExisting[s.orig] = finalI;
    if (dateToFinal[s.date] == null) dateToFinal[s.date] = finalI;
  });

  // Reindex referensi langkah pada PT existing + flag (pakai indeks lama → baru).
  const remap = (oldStep: number) => (remapExisting[oldStep] != null ? remapExisting[oldStep] : oldStep);
  for (const cid of existing.companyOrder) {
    const c = existing.companies[cid];
    for (const v of c.versions) v.step = remap(v.step);
    for (const h of c.history) h.step = remap(h.step);
    for (const d of c.documents) if (typeof d.step === "number") d.step = remap(d.step);
  }
  for (const f of existing.staticFlags) if (typeof f.from === "number") f.from = remap(f.from);

  // PT baru.
  const newId = mkId(short || cname, usedIds);
  const versions: Version[] = [];
  const history: HistoryEntry[] = [];
  const documents: DocEntry[] = [];
  docs.forEach((d, i) => {
    const stepIdx = dateToFinal[docDates[i]];
    const holders: [string, number][] = (d.shareholders || []).map((s) => {
      const shares = (s.shares != null ? Number(s.shares) : (s.percentage != null ? Number(s.percentage) : 0)) || 0;
      return [getId(s.name), shares];
    });
    const officers: [string, string][] = (d.management || []).map((m) => [getId(m.name), m.role || "Pengurus"]);
    versions.push({
      step: stepIdx, dasar: Number(d.modal_dasar) || 0, ditempatkan: Number(d.modal_ditempatkan) || 0,
      nominal: Number(d.nominal_per_share) || 0, holders, officers,
      srcHold: `Ekstraksi AI${d.filename ? " — " + d.filename : ""}`,
    });
    const isFirst = i === 0;
    const hasKbli = (d.kbli || []).length > 0;
    const kind: DocEntry["kind"] = isFirst ? "pendirian" : (hasKbli && (d.shareholders || []).length === 0 ? "nib" : "perubahan");
    documents.push({
      kind,
      label: `${kind === "pendirian" ? "Akta Pendirian" : kind === "nib" ? "NIB / Lampiran" : "Akta Perubahan"}${d.deed_number ? " No. " + d.deed_number : ""}`,
      date: d.deed_date || undefined, url: null, storagePath: d.storage_path || undefined, step: stepIdx,
    });
    history.push({
      step: stepIdx, t: isFirst ? "Pendirian (AI)" : "Perubahan (AI)",
      p: d.notes || [d.deed_number ? `Akta No. ${d.deed_number}` : null, d.deed_date ? `tgl ${d.deed_date}` : null].filter(Boolean).join(" ") || "Ekstraksi AI",
      s: "Diusulkan AI, ditinjau konsultan",
    });
  });

  const maxOrder = existing.companyOrder.reduce((m, cid) => Math.max(m, existing.companies[cid].order), -1);
  existing.companies[newId] = {
    id: newId, name: cname, short, cat, order: maxOrder + 1,
    kedudukan: domicile, alamat: null, operasional: null, kpp: null,
    kbli, kbliSrc: "Ekstraksi AI (gabungan dokumen)", versions, history, documents,
  };
  existing.companyOrder.push(newId);
  existing.steps = newSteps;
  return existing;
}

// Auto-deteksi jenis dokumen dari hasil ekstraksi (bisa dikoreksi user via dropdown).
export function detectDocKind(d: ExtractionDoc, isFirst: boolean): DocEntry["kind"] {
  if (d.doc_kind) return d.doc_kind;
  const hasSH = (d.shareholders || []).length > 0;
  const hasKbli = (d.kbli || []).length > 0;
  if (isFirst && hasSH) return "pendirian";
  if (hasKbli && !hasSH) return "nib";
  if (hasSH) return "perubahan";
  return "npwp";
}

function docKindLabel(kind: DocEntry["kind"], deedNumber?: string | null): string {
  const base = kind === "pendirian" ? "Akta Pendirian" : kind === "nib" ? "NIB / Lampiran" : kind === "npwp" ? "NPWP" : "Akta Perubahan";
  return `${base}${deedNumber ? " No. " + deedNumber : ""}`;
}

// Susulkan/lengkapi dokumen ke SATU PT yang sudah ada di dalam grup. AI membaca
// dokumen lalu melengkapi: arsip dokumen + KBLI + kedudukan + (bila ada) perubahan
// saham/pengurus sebagai tahap baru pada timeline.
export function mergeExtractionsIntoCompany(existingIn: BuiltGroup, companyId: string, docsIn: ExtractionDoc[]): BuiltGroup {
  const existing: BuiltGroup = JSON.parse(JSON.stringify(existingIn));
  const c = existing.companies[companyId];
  if (!c) return existing;
  const docs = [...docsIn].sort((a, b) => (a.deed_date || "").localeCompare(b.deed_date || ""));

  // Resolusi orang terhadap grup (dedupe nama+varian).
  const usedIds = new Set<string>([...existing.personOrder, ...existing.companyOrder]);
  const persons = existing.persons;
  const personOrder = existing.personOrder;
  const nameToId: Record<string, string> = {};
  for (const pid of personOrder) {
    const p = persons[pid];
    if (!p) continue;
    nameToId[(p.name || "").trim().toUpperCase()] = pid;
    for (const v of (p.variants || [])) nameToId[(v || "").trim().toUpperCase()] = pid;
  }
  const getId = (nm: string) => {
    const key = (nm || "").trim().toUpperCase();
    if (nameToId[key]) return nameToId[key];
    const id = mkId(nm, usedIds);
    persons[id] = { id, name: (nm || "").trim() || "Tanpa nama", variants: [] };
    personOrder.push(id);
    nameToId[key] = id;
    return id;
  };

  // Klasifikasi: dokumen yang MENGUBAH kepemilikan (punya pemegang saham & tanggal akta)
  // = peristiwa baru → boleh menambah langkah timeline. Dokumen data pelengkap
  // (NPWP/NIB/tanpa perubahan saham) = bagian dari peristiwa pendirian → TIDAK menambah
  // langkah; ditempelkan ke langkah pendirian PT ini.
  const isEventDoc = (d: ExtractionDoc) => (d.shareholders || []).length > 0 && !!d.deed_date;

  const existingSteps = existing.steps;
  const existingDateSet = new Set(existingSteps.map((s) => s.date));
  const docByDate: Record<string, ExtractionDoc> = {};
  const eventDates: string[] = [];
  docs.forEach((d) => { if (isEventDoc(d)) { const dt = d.deed_date as string; docByDate[dt] = d; eventDates.push(dt); } });
  const newDates = Array.from(new Set(eventDates.filter((dt) => !existingDateSet.has(dt))));

  type Slot = { date: string; step: Step; orig?: number };
  const slots: Slot[] = [];
  existingSteps.forEach((s, i) => slots.push({ date: s.date, step: s, orig: i }));
  for (const dt of newDates) {
    const d = docByDate[dt];
    const deedInfo = d ? [d.deed_number ? `Akta No. ${d.deed_number}` : null, `tgl ${dt}`].filter(Boolean).join(" ") : "";
    slots.push({ date: dt, step: { date: dt, label: `${c.name}${deedInfo ? " — " + deedInfo : ""}`, detail: (d?.notes) || deedInfo || "Perubahan (AI)" } });
  }
  const order = slots.map((_, i) => i).sort((a, b) => slots[a].date.localeCompare(slots[b].date) || (a - b));
  const newSteps: Step[] = order.map((i) => slots[i].step);
  const remapExisting: Record<number, number> = {};
  const dateToFinal: Record<string, number> = {};
  order.forEach((slotI, finalI) => {
    const s = slots[slotI];
    if (s.orig != null) remapExisting[s.orig] = finalI;
    if (dateToFinal[s.date] == null) dateToFinal[s.date] = finalI;
  });
  const remap = (oldStep: number) => (remapExisting[oldStep] != null ? remapExisting[oldStep] : oldStep);
  for (const cid of existing.companyOrder) {
    const cc = existing.companies[cid];
    for (const v of cc.versions) v.step = remap(v.step);
    for (const h of cc.history) h.step = remap(h.step);
    for (const d of cc.documents) if (typeof d.step === "number") d.step = remap(d.step);
  }
  for (const f of existing.staticFlags) if (typeof f.from === "number") f.from = remap(f.from);
  existing.steps = newSteps;

  // Langkah pendirian PT (langkah paling awal PT ini muncul) untuk menempelkan dokumen data.
  const estStep = c.versions.length ? Math.min(...c.versions.map((v) => v.step)) : 0;

  // Lengkapi data PT dari tiap dokumen.
  docs.forEach((d) => {
    const isEvent = isEventDoc(d);
    const stepIdx = isEvent ? dateToFinal[d.deed_date as string] : estStep;
    const kind = detectDocKind(d, false);

    // Lengkapi identitas PT bila masih kosong.
    if ((!c.kedudukan || c.kedudukan === "—") && d.domicile) c.kedudukan = d.domicile;
    if (!c.alamat && d.address) { c.alamat = d.address; c.alamatSrc = `Ekstraksi AI (dokumen susulan)`; }
    if (!c.kpp && d.kpp) { c.kpp = d.kpp; c.kppNote = "dari NPWP (AI)"; }
    if (!c.npwp && d.npwp) c.npwp = d.npwp;

    // Merge KBLI.
    for (const k of (d.kbli || [])) {
      if (!k.code) continue;
      const ex = c.kbli.find((x) => x.code === k.code);
      if (ex) { if (!ex.title && k.title) ex.title = k.title; if (k.main) ex.main = true; }
      else c.kbli.push({ code: k.code, title: k.title || k.code, unverified: false, main: !!k.main });
    }
    if ((d.kbli || []).length) c.kbliSrc = "Ekstraksi AI (dokumen susulan)";

    // Arsip dokumen: isi placeholder "Belum ada" bila ada; jika tidak, tambah baris baru (dedupe).
    const isMissing = (x: DocEntry) => !x.url && !x.storagePath;
    const placeholder = c.documents.find((x) => x.kind === kind && isMissing(x));
    const exactDup = c.documents.find((x) => x.kind === kind && !!x.storagePath && (x.date || "") === (d.deed_date || ""));
    if (placeholder) {
      placeholder.storagePath = d.storage_path || placeholder.storagePath;
      if (d.deed_date) placeholder.date = d.deed_date;
      placeholder.step = stepIdx;
      placeholder.label = docKindLabel(kind, d.deed_number);
    } else if (!exactDup) {
      c.documents.push({ kind, label: docKindLabel(kind, d.deed_number), date: d.deed_date || undefined, url: null, storagePath: d.storage_path || undefined, step: stepIdx });
    }

    // Riwayat.
    c.history.push({
      step: stepIdx, t: `Dokumen susulan (AI) — ${docKindLabel(kind, d.deed_number)}`,
      p: d.notes || [d.deed_number ? `Akta No. ${d.deed_number}` : null, d.deed_date ? `tgl ${d.deed_date}` : null].filter(Boolean).join(" ") || "Ekstraksi AI",
      s: "Diusulkan AI, ditinjau konsultan",
    });

    // Hanya dokumen peristiwa (perubahan saham) yang menambah/mengubah versi.
    if (isEvent) {
      const hasMgmt = (d.management || []).length > 0;
      const prev = c.versions.filter((v) => v.step <= stepIdx).sort((a, b) => a.step - b.step).pop();
      const holders: [string, number][] = (d.shareholders || []).map((s) => [getId(s.name), (s.shares != null ? Number(s.shares) : (s.percentage != null ? Number(s.percentage) : 0)) || 0]);
      const officers: [string, string][] = hasMgmt
        ? (d.management || []).map((m) => [getId(m.name), m.role || "Pengurus"])
        : (prev?.officers || []);
      const nv: Version = {
        step: stepIdx,
        dasar: Number(d.modal_dasar) || prev?.dasar || 0,
        ditempatkan: Number(d.modal_ditempatkan) || prev?.ditempatkan || 0,
        nominal: Number(d.nominal_per_share) || prev?.nominal || 0,
        holders, officers, srcHold: `Ekstraksi AI (susulan)${d.filename ? " — " + d.filename : ""}`,
      };
      const at = c.versions.findIndex((v) => v.step === stepIdx);
      if (at >= 0) c.versions[at] = nv; else c.versions.push(nv);
    }
  });

  syncDocShortageFlag(existing, companyId);
  c.versions.sort((a, b) => a.step - b.step);
  c.documents.sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
  c.history.sort((a, b) => a.step - b.step);
  return existing;
}

// Perbarui/hapus flag kekurangan dokumen (DOK_BELUM_ADA) berdasarkan kelengkapan nyata PT.
function syncDocShortageFlag(g: BuiltGroup, cid: string) {
  const c = g.companies[cid];
  if (!c) return;
  const has = (kind: DocEntry["kind"]) => c.documents.some((d) => d.kind === kind && (d.url || d.storagePath));
  const missing: string[] = [];
  if (!has("nib")) missing.push("NIB");
  if (!has("npwp")) missing.push("NPWP");
  if (!c.kpp) missing.push("KPP");
  if (!c.alamat) missing.push("alamat lengkap");
  const idx = g.staticFlags.findIndex((f) => f.code === "DOK_BELUM_ADA" && f.company === cid);
  if (idx < 0) return; // hanya kelola flag seed yang sudah ada, jangan buat baru
  if (missing.length === 0) { g.staticFlags.splice(idx, 1); return; }
  g.staticFlags[idx].msg = `Dokumen/data ${c.short} belum lengkap: ${missing.join(", ")} belum tersedia.`;
}
// Daftarkan grup beserta struktur datanya (grup yang datanya tersimpan di workspace).
// Buang langkah "yatim" (tidak direferensikan versi PT mana pun) yang mungkin tersimpan
// dari data lama; dokumen/riwayat yang menunjuk ke sana dialihkan ke langkah pendirian PT.
export function normalizeGroupSteps(g: BuiltGroup): BuiltGroup {
  const steps = g.steps || [];
  const companies = g.companies || {};
  const referenced = new Set<number>();
  for (const cid of g.companyOrder || []) for (const v of (companies[cid]?.versions || [])) referenced.add(v.step);
  const orphans = steps.map((_, i) => i).filter((i) => !referenced.has(i));
  if (!orphans.length) return g;
  const kept = steps.map((_, i) => i).filter((i) => referenced.has(i));
  const oldToNew: Record<number, number> = {};
  kept.forEach((old, ni) => { oldToNew[old] = ni; });
  const nearest = (fr: number) => { if (oldToNew[fr] != null) return oldToNew[fr]; const below = kept.filter((k) => k <= fr); return below.length ? oldToNew[below[below.length - 1]] : 0; };
  for (const cid of g.companyOrder || []) {
    const c = companies[cid]; if (!c) continue;
    const est = c.versions.length ? Math.min(...c.versions.map((v) => v.step)) : (kept[0] ?? 0);
    const fix = (step: number) => (oldToNew[step] != null ? oldToNew[step] : (oldToNew[est] ?? 0));
    for (const v of c.versions) v.step = oldToNew[v.step] ?? 0;
    for (const h of c.history) h.step = fix(h.step);
    for (const d of c.documents) if (typeof d.step === "number") d.step = fix(d.step);
  }
  for (const f of g.staticFlags || []) if (typeof f.from === "number") f.from = nearest(f.from);
  g.steps = kept.map((i) => steps[i]);
  return g;
}

export function registerGroupWithData(id: string, data: BuiltGroup) {
  REGISTRY[id] = normalizeGroupSteps(data);
  if (!ORDER.includes(id)) ORDER.push(id);
}

export function registerCustomGroup(id: string, name: string) {
  if (!REGISTRY[id]) { REGISTRY[id] = emptyGroup(name); ORDER.push(id); }
}
export function deleteGroup(id: string) {
  if (REGISTRY[id]) delete REGISTRY[id];
  const i = ORDER.indexOf(id);
  if (i >= 0) ORDER.splice(i, 1);
}
export function renameGroup(id: string, name: string) {
  if (REGISTRY[id]) REGISTRY[id].name = name;
}
export function getGroupList(): { id: string; name: string }[] {
  return ORDER.filter((id) => REGISTRY[id]).map((id) => ({ id, name: REGISTRY[id].name }));
}
export function groupExists(id: string): boolean { return !!REGISTRY[id]; }

// Reset registry ke kondisi awal (dipakai saat ganti user / muat ulang workspace)
export function resetRegistry() {
  for (const k of Object.keys(REGISTRY)) delete REGISTRY[k];
  ORDER.length = 0;
  setActiveGroup("");
}

const EMPTY_ACTIVE = emptyGroup("—");

// Live bindings — grup aktif (mulai kosong; diisi setelah workspace user dimuat)
export let PERSONS: Record<string, Person> = EMPTY_ACTIVE.persons;
export let PERSON_ORDER: string[] = EMPTY_ACTIVE.personOrder;
export let CATEGORIES: Record<string, Category> = EMPTY_ACTIVE.categories;
export let STEPS: Step[] = EMPTY_ACTIVE.steps;
export let STATIC_FLAGS: StaticFlag[] = EMPTY_ACTIVE.staticFlags;
export let COMPANIES: Record<string, Company> = EMPTY_ACTIVE.companies;
export let COMPANY_ORDER: string[] = EMPTY_ACTIVE.companyOrder;

export function setActiveGroup(id: string) {
  const g = REGISTRY[id] || (ORDER.length ? REGISTRY[ORDER[0]] : EMPTY_ACTIVE) || EMPTY_ACTIVE;
  PERSONS = g.persons;
  PERSON_ORDER = g.personOrder;
  CATEGORIES = g.categories;
  STEPS = g.steps;
  STATIC_FLAGS = g.staticFlags;
  COMPANIES = g.companies;
  COMPANY_ORDER = g.companyOrder;
}

// kategori default seluruh PT di semua grup yang punya data (untuk warna kartu awal)
export function defaultCompanyCat(): Record<string, string> {
  const o: Record<string, string> = {};
  for (const gid of ORDER) {
    const g = REGISTRY[gid];
    if (!g) continue;
    for (const cid of g.companyOrder) o[cid] = g.companies[cid].cat;
  }
  return o;
}

// ---------------- Util ----------------
// Saran kategori dari kode KBLI (berbasis divisi 2-digit KBLI + override khusus).
// Bersifat SARAN saja; user tetap bisa mengubah kategori manual.
const KBLI_CATEGORY_OVERRIDES: Record<string, string> = {
  "64200": "holding",        // Aktivitas Perusahaan Holding
  "64931": "keuangan",       // Modal Ventura
  "66311": "keuangan",       // Manajer Investasi
  "70209": "profesional",    // Konsultasi Manajemen
  "47725": "farmasi_alkes",  // Eceran alat farmasi & alkes
  "47726": "farmasi_alkes",
  "46441": "farmasi_alkes",  // Besar farmasi & alkes
  "46442": "farmasi_alkes",
};
export function suggestCategoryFromKbli(code: string): string | null {
  if (!code) return null;
  if (KBLI_CATEGORY_OVERRIDES[code]) return KBLI_CATEGORY_OVERRIDES[code];
  const div = parseInt(code.slice(0, 2), 10);
  if (isNaN(div)) return null;
  if (div >= 1 && div <= 3) return "agri";
  if (div >= 5 && div <= 9) return "tambang";
  if (div >= 10 && div <= 33) return "manufaktur";
  if (div === 35) return "energi";
  if (div >= 36 && div <= 39) return "air_limbah";
  if (div >= 41 && div <= 43) return "konstruksi";
  if (div === 45) return "otomotif";
  if (div === 46) return "grosir";
  if (div === 47) return "retail";
  if (div >= 49 && div <= 53) return "logistik";
  if (div === 55) return "akomodasi";
  if (div === 56) return "fnb";
  if (div >= 58 && div <= 60) return "media";
  if (div >= 61 && div <= 63) return "infokom";
  if (div >= 64 && div <= 66) return "keuangan";
  if (div === 68) return "properti";
  if (div >= 69 && div <= 75) return "profesional";
  if (div >= 77 && div <= 82) return "jasa_penunjang";
  if (div === 85) return "pendidikan";
  if (div === 86) return "health";
  if (div >= 87 && div <= 88) return "sosial";
  if (div >= 90 && div <= 93) return "hiburan";
  if (div >= 94 && div <= 96) return "jasa_lain";
  return null;
}

export function isPerson(id: string): boolean { return !!PERSONS[id]; }
export function entityName(id: string): string { return PERSONS[id]?.name ?? COMPANIES[id]?.name ?? id; }
export function formatRupiah(n: number): string { return "Rp" + n.toLocaleString("id-ID"); }
export function rupiahShort(n: number): string {
  if (n >= 1000000000) return "Rp" + (n / 1000000000).toLocaleString("id-ID", { maximumFractionDigits: 2 }) + " M";
  if (n >= 1000000) return "Rp" + (n / 1000000).toLocaleString("id-ID", { maximumFractionDigits: 0 }) + " jt";
  if (n >= 1000) return "Rp" + (n / 1000).toLocaleString("id-ID", { maximumFractionDigits: 0 }) + " rb";
  return "Rp" + n.toLocaleString("id-ID");
}
export function ddmmyyyy(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}
